/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-empty-function */
import { useEffect, useState } from "react";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Toolbar } from "primereact/toolbar";
import { SplitButton } from "primereact/splitbutton";
import { Card } from "primereact/card";
import { UpdateConfiguration } from "../services/UpdateConfiguration";
import { GetServiceConfiguration } from "../services/GetServiceConfiguration";
import { Skeleton } from "primereact/skeleton";
import { Dialog } from "primereact/dialog";

interface FormData {
    [key: string]: any;
}

const useUnsavedChanges = (formData: FormData, unchangedData: FormData) => {
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const isDataChanged =
            JSON.stringify(formData) !== JSON.stringify(unchangedData);
        setHasUnsavedChanges(isDataChanged);
    }, [formData, unchangedData]);

    const handleSave = () => {
        setLoading(true);
        const trimmedJsonString = JSON.stringify(formData, null, 0);
        UpdateConfiguration("JIRA", trimmedJsonString).finally(() => {
            setLoading(false);
            setHasUnsavedChanges(false);
        });
    };

    return { hasUnsavedChanges, handleSave, loading };
};

export const Configuration = () => {
    const [formData, setFormData] = useState<FormData>({});
    const [backupFiles, setBackupFiles] = useState<FormData[]>([]);
    const [unchangedData, setUnchangedData] = useState<FormData>({});
    const [addNewRow, setAddNewRow] = useState<boolean>(false);
    const [newConfigKey, setNewConfigKey] = useState<string>("");
    const [newConfigValue, setNewConfigValue] = useState<string>("");
    const [enabledInputs, setEnabledInputs] = useState<string[]>([]);
    const dataTypes = ["String", "Number", "Array", "Object", "Boolean"];
    const [dataTypesMap, setDataTypesMap] = useState<{ [key: string]: string }>(
        {}
    );

    const { hasUnsavedChanges, handleSave, loading } = useUnsavedChanges(
        formData,
        unchangedData
    );

    useEffect(() => {
        GetServiceConfiguration("JIRA").then((data) => {
            const config = JSON.parse(data.config);
            const sortedKeys = Object.keys(config).sort();
            const sortedFormData: FormData = {};

            sortedKeys.forEach((key) => {
                sortedFormData[key] = config[key];
            });

            setFormData(sortedFormData);
            setBackupFiles(data.files);
            setUnchangedData(sortedFormData);
        });
    }, []);

    const getDefaultValue = (value: string | number | any[]): string => {
        if (Array.isArray(value)) {
            return "Array";
        } else if (typeof value === "boolean") {
            return "Boolean";
        } else if (typeof value === "number") {
            return "Number";
        } else if (value === "true" || value === "false") {
            return "String";
        } else {
            if (typeof value === "string") {
                return "String";
            }

            try {
                JSON.parse(value);
                return "Array";
            } catch (error) {
                return "String";
            }
        }
    };

    const handleDataTypeChange = (key: string, value: string) => {
        const updatedDataTypesMap = { ...dataTypesMap };
        updatedDataTypesMap[key] = value;
        setDataTypesMap(updatedDataTypesMap);

        const updatedFormData = { ...formData };

        if (value === "Array") {
            try {
                updatedFormData[key] = JSON.parse(
                    JSON.stringify(updatedFormData[key])
                );
            } catch (error) {
                updatedFormData[key] = [];
            }
        } else if (value === "Boolean") {
            updatedFormData[key] = Boolean(updatedFormData[key]);
        } else if (value === "Number") {
            updatedFormData[key] = Number(updatedFormData[key]);
        } else {
            updatedFormData[key] = String(updatedFormData[key]);
        }

        setFormData(updatedFormData);
    };

    const handleInputChange = (key: string, value: string) => {
        const updatedFormData = { ...formData };
        const dataType =
            dataTypesMap[key] || getDefaultValue(updatedFormData[key]);

        if (dataType === "Array") {
            try {
                updatedFormData[key] = JSON.parse(value);
            } catch (error) {
                updatedFormData[key] = value;
            }
        } else if (dataType === "Boolean") {
            updatedFormData[key] = value.toLowerCase() === "true";
        } else if (dataType === "Number") {
            updatedFormData[key] = Number(value);
        } else {
            updatedFormData[key] = value;
        }

        setFormData(updatedFormData);
    };

    const removeDataByKey = (key: string) => {
        setFormData((prevData) => {
            const newData = { ...prevData };
            delete newData[key];
            return newData;
        });
    };

    const enableInput = (key: string) => {
        if (enabledInputs.includes(key)) {
            setEnabledInputs((prevEnabledInputs) =>
                prevEnabledInputs.filter((inputKey) => inputKey !== key)
            );
        } else {
            setEnabledInputs((prevEnabledInputs) => [
                ...prevEnabledInputs,
                key
            ]);
        }
    };

    const renderInputs = () => {
        if (formData && Object.keys(formData).length > 0) {
            return Object.keys(formData).map((key) => {
                const defaultValue = getDefaultValue(formData[key]);

                return (
                    <div
                        style={{ width: "100%" }}
                        className="mt-3 flex align-items-center"
                        key={key}
                    >
                        <div style={{ minWidth: 400 }}>
                            <label htmlFor={key}>{key}</label>
                        </div>

                        <InputText
                            id={key}
                            value={
                                Array.isArray(formData[key])
                                    ? `[${formData[key]
                                          .map((value: any) => `"${value}"`)
                                          .join(", ")}]`
                                    : formData[key]
                            }
                            onChange={(e) =>
                                handleInputChange(key, e.target.value)
                            }
                            className="ml-2"
                            style={{ width: 300 }}
                            disabled={!enabledInputs.includes(key)}
                        />

                        <Dropdown
                            value={dataTypesMap[key] || defaultValue}
                            options={dataTypes}
                            onChange={(e) => handleDataTypeChange(key, e.value)}
                            className="ml-2"
                            style={{ width: 150 }}
                            disabled={!enabledInputs.includes(key)}
                        />
                        <Button
                            label="Delete"
                            icon="pi pi-times"
                            className="ml-4 p-button-warning"
                            loading={loading}
                            onClick={() => {
                                removeDataByKey(key);
                            }}
                        />

                        <Button
                            label="Edit"
                            icon="pi pi-pencil"
                            className="ml-4 p-button-secondary"
                            onClick={() => {
                                enableInput(key);
                            }}
                            disabled={enabledInputs.includes(key)}
                        />
                    </div>
                );
            });
        } else {
            return Array.from({ length: 20 }).map((_, index) => (
                <div
                    style={{ width: "100%" }}
                    className="mt-3 flex align-items-center"
                    key={index}
                >
                    <div style={{ minWidth: 400 }}>
                        <Skeleton height="50px"></Skeleton>
                    </div>

                    <Skeleton
                        className="ml-2"
                        width="300px"
                        height="50px"
                    ></Skeleton>
                </div>
            ));
        }
    };

    const undoChanges = () => {
        setFormData([]);
        setFormData(unchangedData);
    };

    const onBackupReset = (backupFile: string) => {
        setFormData([]);
        setFormData(JSON.parse(backupFile));
    };

    const getResetBackups = () => {
        const backupArray = [
            {
                label: "Undo",
                icon: "pi pi-refresh",
                command: undoChanges
            }
        ];
        backupFiles.forEach((backup) => {
            backupArray.push({
                label: backup.filename,
                icon: "pi pi-backward",
                command: () => onBackupReset(backup.content)
            });
        });

        return backupArray;
    };

    const startContent = (
        <>
            <Button
                label="New"
                icon="pi pi-plus"
                loading={loading}
                className="p-button-success"
                onClick={() => setAddNewRow(true)}
            />
            <Button
                label="Save"
                icon="pi pi-save"
                className="ml-2"
                loading={loading}
                disabled={!hasUnsavedChanges}
                onClick={() => {
                    handleSave();
                    setUnchangedData(formData);
                    setEnabledInputs([]);
                }}
            />
        </>
    );

    const endContent = (
        <SplitButton
            icon="pi pi-times"
            label="Undo changes"
            disabled={loading}
            className="p-button-danger"
            onClick={undoChanges}
            model={getResetBackups()}
        ></SplitButton>
    );

    return (
        <div className="flex flex-column py-4">
            <h1>Orchestration Configuration</h1>

            <div className="mt-5">
                <Card>
                    <Accordion activeIndex={0}>
                        <AccordionTab header="Jira/ASIMS Configuration">
                            <Toolbar left={startContent} right={endContent} />
                            {hasUnsavedChanges && (
                                <div className="mt-4" style={{ color: "red" }}>
                                    You have unsaved changes.
                                </div>
                            )}
                            <form className="mt-3">{renderInputs()}</form>
                        </AccordionTab>
                    </Accordion>
                </Card>
            </div>

            <Dialog
                header={<h2>Add new key/value</h2>}
                style={{ width: "50vh" }}
                visible={addNewRow}
                modal
                footer={
                    <Button
                        label="Save"
                        icon="pi pi-save"
                        className="ml-2"
                        loading={loading}
                        disabled={!newConfigKey || newConfigKey === ""}
                        onClick={() => {
                            handleInputChange(newConfigKey, newConfigValue);
                            setNewConfigValue("");
                            setNewConfigKey("");
                            setAddNewRow(false);
                        }}
                    />
                }
                onHide={() => setAddNewRow(false)}
            >
                <div>
                    <InputText
                        placeholder="Config Key"
                        value={newConfigKey}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setNewConfigKey(e.target.value)
                        }
                    />

                    <InputText
                        placeholder="Config Value"
                        className="ml-2"
                        value={newConfigValue}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setNewConfigValue(e.target.value)
                        }
                    />
                </div>
            </Dialog>
        </div>
    );
};
